import { SelectOption } from '@navstation-portal/shared/data-model/common';
import { DEFAULT_UTC_OFFSET, SELECTABLE_UTC_OFFSETS, TimezoneOption } from '@navstation-portal/shared/data-model/time';
import { formatUtcOffset, resolveUtcOffset, tvaTimezoneToUtcOffset } from './utc-offset';

/** Gives an offset with its own label. */
export function utcOffsetToTimezoneOption(offset: number): TimezoneOption {
  return { name: formatUtcOffset(offset), offset };
}

/**
 * The offsets a user can select, in ascending order.
 *
 * The type is mutable because the select controls declare a mutable array
 * input. The offsets themselves stay readonly. This array is a new array that
 * the module derives from them at load.
 */
export const TIMEZONE_OPTIONS: TimezoneOption[] = SELECTABLE_UTC_OFFSETS.map(utcOffsetToTimezoneOption);

/** The timezone a user has until the user selects a different one. */
export const DEFAULT_TIMEZONE: TimezoneOption = utcOffsetToTimezoneOption(DEFAULT_UTC_OFFSET);

/**
 * Gives the same option in the shape the select controls bind to.
 *
 * `SelectOption.code` has the type `unknown`. An offset therefore travels in a
 * `TimezoneOption`, and this adapter sits at the component edge. The offset
 * stays a number here. A comparison against a string is still a compile error
 * in the code behind the edge.
 */
export function timezoneOptionToSelectOption(option: TimezoneOption): SelectOption {
  return { name: option.name, code: option.offset };
}

/** The offsets a user can select, in the shape the select controls bind to. */
export const TIMEZONE_SELECT_OPTIONS: SelectOption[] = TIMEZONE_OPTIONS.map(timezoneOptionToSelectOption);

/**
 * Gives the select option for an offset, and for a retired offset too.
 *
 * A stored report shows the offset it was saved against, even after the
 * dropdown stops offering that offset.
 */
export function utcOffsetToSelectOption(offset: number | undefined): SelectOption | undefined {
  if (offset == null) return undefined;

  return timezoneOptionToSelectOption(utcOffsetToTimezoneOption(offset));
}

/**
 * Gives the select option a cloud API timezone key names, or nothing.
 *
 * Use this where the wire value is the key text and nothing else. A stored
 * report's timezone field is one such path. A value carried over from the
 * previous report is another. Any other input gives nothing, and that includes
 * the offset number.
 */
export function tvaTimezoneToSelectOption(key: unknown): SelectOption | undefined {
  if (typeof key !== 'string') return undefined;

  return utcOffsetToSelectOption(tvaTimezoneToUtcOffset(key));
}

/**
 * Gives the select option for a timezone the cloud API sent, or nothing. It
 * accepts a key, a number and a numeric string.
 *
 * Use this on the offshore read path. The response declares one type and holds
 * a different value at run time, and nobody can reach the API to find which one
 * is correct. Every offshore timezone field goes through this one function. No
 * field matches an encoding of its own.
 */
export function resolveTimezoneSelectOption(value: unknown): SelectOption | undefined {
  return utcOffsetToSelectOption(resolveUtcOffset(value));
}
